import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { Tradable } from "@models/interfaces/tradable";
import { Sort } from "@angular/material/sort";
import { combineLatest, EMPTY, map, Observable } from "rxjs";
import { Fiat } from "@models";
import { Index } from "@models/asset/index/index.model";
import { AccountService } from "@modules/account/services/account.service";
import { ASSET_LIST_FILTERS, ASSET_LIST_HEADLINES, ASSET_LIST_TIMEFRAMES } from "@modules/trade/constants/constants";
import { getTradableChange, isDigiAsset } from "@modules/trade/helpers/helper";
import { SavingsPlanAssetListService } from "@modules/trade/services/savings-plan-asset-list.service";
import { AssetsTradeScrollService } from "@main-services/assets-trade-scroll.service";
import { BpOption } from "@models/ui-components/bp-option";
import { Dictionary } from "@helpers/types";
import { TradeAgainService } from "@modules/trade/services/trade-again.service";
import { SavingsListFacade } from "@modules/shared/components/savings-list/savings-list.facade";
import { SegmentService } from "@main-services/segment.service";
import {
	ALL_FILTERS_AVAILABLE,
	API_CRYPTO_TYPE,
	API_ETC_TYPE,
	API_ETF_TYPE,
	API_INDEX_TYPE,
	API_LEVERAGED_TOKEN_TYPE,
	API_METAL_TYPE,
	API_SECURITY_TYPE,
	API_STOCK_TYPE,
	SECURITY_ASSET_GROUP_LIST,
	STEELCOIN_INTERNAL_ID
} from "@helpers/constants";
import { Timespan } from "@helpers/const/timespan";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { SecurityToken } from "@models/asset/cryptocoins/currencies/security_token";
import { RudderstackService } from "@main-services/rudderstack.service";
import { FEAT_GATES, StatsigClientService } from "@core-services/statsig/statsig.service";

@Component({
	selector: "savings-plan-asset-list",
	templateUrl: "./savings-plan-asset-list.component.html",
	styleUrls: ["./savings-plan-asset-list.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [SavingsPlanAssetListService, TradeAgainService, AssetsTradeScrollService],
	standalone: false
})
export class SavingsPlanAssetListComponent implements OnInit {
	@Input() public tradables$: Observable<Tradable[]> = EMPTY;
	@Output() public assetClicked: EventEmitter<Tradable> = new EventEmitter<Tradable>();

	public scrollToTop: boolean = false;

	private readonly areSecurityTokenActive$: Observable<boolean> = this.launchDarklyService.isActive$(
		LD_FLAG.SECURITY_TOKENS
	);

	private readonly hideSteelCoin$: Observable<boolean> = this.statsigService.isFeatureActive(
		FEAT_GATES.REMOVE_STEELCOIN
	);

	public userHasNoBciSavingsPlans$: Observable<boolean> = this.savingsListFacade.userHasNoBciSavingsPlans$();
	//filter out security tokens if the feature flag is not active
	//TODO: remove this filter once the feature flag is removed
	public mostPopularList$: Observable<Tradable[]> = this.filterWithSecurityTokenFF$(
		this.tradeAgainService.fetchMostPopularList$()
	);
	public defaultFiat: Fiat = this.accountService.getDefaultFiat();

	// infinite scroll
	public isLoading$ = this.savingsPlanAssetListService.getLoadingObservable$();
	public hasError$ = this.savingsPlanAssetListService.getErrorObservable$();

	public readonly filters$: Observable<BpOption[]> = this.areSecurityTokenActive$.pipe(
		map((isActive) => {
			const assetList = ASSET_LIST_FILTERS.filter(
				(f) =>
					f.value !== "" &&
					f.value !== API_LEVERAGED_TOKEN_TYPE &&
					f.value !== API_STOCK_TYPE &&
					f.value !== API_ETF_TYPE &&
					f.value !== API_METAL_TYPE &&
					f.value !== API_ETC_TYPE &&
					f.value !== API_SECURITY_TYPE
			);
			if (!isActive) {
				return assetList.filter((f) => f.value !== API_SECURITY_TYPE);
			}
			return assetList;
		})
	);
	public readonly timeframes = ASSET_LIST_TIMEFRAMES;
	public readonly headlines: Dictionary<string> = ASSET_LIST_HEADLINES;

	public selectedFilter: BpOption = ASSET_LIST_FILTERS.filter((a) => a.value === API_CRYPTO_TYPE)[0];
	public searchValue: string = "";
	public filterValue: string = API_CRYPTO_TYPE;
	public timeframeValue: Timespan = this.timeframes[0].value;
	public sortValue: Sort = null;

	constructor(
		private readonly accountService: AccountService,
		private readonly savingsPlanAssetListService: SavingsPlanAssetListService,
		private readonly tradeAgainService: TradeAgainService,
		private readonly savingsListFacade: SavingsListFacade,
		private readonly segmentService: SegmentService,
		private readonly rudderstackService: RudderstackService,
		private readonly launchDarklyService: LaunchDarklyService,
		private readonly statsigService: StatsigClientService
	) {}

	public ngOnInit(): void {
		//filter out security tokens if the feature flag is not active
		//TODO: remove this filter once the feature flag is removed
		this.tradables$ = this.filterWithSecurityTokenFF$(this.savingsPlanAssetListService.getList$());

		this.savingsPlanAssetListService.initList("buy", this.buildFilters());
	}

	private filterWithSecurityTokenFF$(tradables: Observable<Tradable[]>): Observable<Tradable[]> {
		return combineLatest({
			tradables,
			areSecurityTokenActive: this.areSecurityTokenActive$,
			hideSteelCoin: this.hideSteelCoin$
		}).pipe(
			map(({ tradables, areSecurityTokenActive, hideSteelCoin }) => {
				return tradables.filter((tradable) => {
					const currencyType = tradable.getCurrencyType();
					const isSecurityToken = tradable instanceof SecurityToken;
					const isSteelCoin = tradable.getId() === STEELCOIN_INTERNAL_ID;

					if (isDigiAsset(currencyType)) {
						return false;
					}
					if (!areSecurityTokenActive && isSecurityToken) {
						return false;
					}
					if (hideSteelCoin && isSteelCoin) {
						return false;
					}
					return true;
				});
			})
		);
	}

	public bannerClick(): void {
		this.segmentService.track({ name: "Savings BCI Banner Clicked" });
		this.rudderstackService.track({ name: "Savings BCI Banner Clicked" });
		this.onFilterChange({ label: "currency_filter_crypto_indices", value: API_INDEX_TYPE });
		this.selectedFilter = { label: "currency_filter_crypto_indices", value: API_INDEX_TYPE };
	}

	public onScrolledToBottom(): void {
		this.savingsPlanAssetListService.fetchNextPage();
	}

	public onClick(tradable: Tradable): void {
		this.assetClicked.emit(tradable);
	}

	public onFilterChange(option: BpOption): void {
		this.filterValue = option.value;
		this.fetchUpdatedList();
	}

	public onSortChange(sortState: Sort): void {
		this.sortValue = sortState;
		this.fetchUpdatedList();
	}

	public onSearchValueChange(val: string): void {
		this.searchValue = val;
		this.fetchUpdatedList();
	}

	public fetchUpdatedList(): void {
		this.savingsPlanAssetListService.updateList(
			this.sortValue?.active,
			this.sortValue?.direction,
			this.timeframeValue,
			this.buildFilters(),
			this.searchValue
		);

		// scroll the table back to top
		this.scrollToTop = true;
	}

	public refetchList(): void {
		this.savingsPlanAssetListService.refetchList(this.buildFilters());
	}

	public getChangePercent(tradable: Tradable): number {
		return getTradableChange(tradable, this.timeframeValue)?.changePercent;
	}

	public isIndex(tradable: Tradable): boolean {
		return tradable instanceof Index;
	}

	pillsThatNeedRealSecuritiesBanner = SECURITY_ASSET_GROUP_LIST;

	private buildFilters(): string[] {
		if (this.filterValue === "" || this.filterValue === "all") {
			return ALL_FILTERS_AVAILABLE;
		}

		return [this.filterValue];
	}
}
